import {graphql, PageProps} from 'gatsby'
import React from 'react'

import Footer from '@/components/footer'
import Header from '@/components/header'
import SEO from '@/components/seo'
import {HomepageV3RepeatableQuery} from '@/graphql-types'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'
import {Main} from '@helios/shared/components/skip-to-content'
import {useLocale} from '@helios/shared/providers/locale'

import {SliceZone} from './slice-zone'

type Props = PageProps<HomepageV3RepeatableQuery>

export const HomepageV3Repeatable = (props: Props) => {
    const data = props.data.prismicHomepageV3Repeatable?.data
    const currentLocale = useLocale()

    if (!data) {
        return handleNoPrismicData()
    }

    return (
        <>
            <SEO preventIndexing={data.exclude_robots} />

            <Header
                hideLeftNav={currentLocale.isPreLaunch || data.hide_left_nav}
                hideRightNav={currentLocale.isPreLaunch || data.hide_right_nav}
            />

            <Main>
                <SliceZone slices={data.body} />
            </Main>

            <Footer hideEmails={data.hide_emails} hideLinks={data.hide_links} />
        </>
    )
}

export const query = graphql`
    query HomepageV3Repeatable(
        $locale: String!
        $uid: String!
        $imageBreakpoints: [Int]
        $twoColumnsAsymmetric: String
    ) {
        prismicHomepageV3Repeatable(uid: {eq: $uid}, lang: {eq: $locale}) {
            _previewable
            data {
                exclude_robots
                hide_left_nav
                hide_right_nav
                hide_emails
                hide_links
                body {
                    ...HomepageV3RepeatableFeatureBlock
                    ...HomepageV3RepeatableGetStarted
                    ...HomepageV3RepeatableStatistics
                    ...HomepageV3RepeatableCustomerLogosPlusCta
                    ...HomepageV3RepeatableRotatingBanner
                    ...HomepageV3RepeatableVideoTabs
                    ...HomepageV3RepeatableVideoWithOutline
                    ...HomepageV3RepeatableIntegrations
                    ...HomepageV3RepeatableHero
                    ...HomepageV3RepeatableCardGrid
                    ...HomepageV3RepeatableContentPlusBlocks
                    ...HomepageV3RepeatableSocialProof
                    ...HomepageV3RepeatableFAQ
                    ...HomepageV3RepeatablePricing
                    ...HomepageV3RepeatableRatingsPlusHeadline
                    ...HomepageV3RepeatableFeatureGrid
                    ...HomepageV3RepeatableSteps
                    ...HomepageV3RepeatableComponentPlaceholder
                }
            }
        }
    }

    fragment HomepageV3RepeatableComponentPlaceholder on PrismicHomepageV3RepeatableDataBodyComponentPlaceholder {
        __typename
        primary {
            predefined_component {
                type
                document {
                    ...BillsCalculator
                    ...CashbackCalculator
                    ...FeaturesComparisonTable
                }
            }
        }
    }

    fragment HomepageV3RepeatableSteps on PrismicHomepageV3RepeatableDataBodySteps {
        __typename
        primary {
            title
            show_count
        }
        items {
            heading
            content
            background_color
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(
                    breakpoints: $imageBreakpoints
                    sizes: "(max-width: 767px) calc(100vw - 48px), (max-width: 1200px) calc(100vw / 2 - 48px), (max-width: 1439px) calc(100vw / 2 - 120px), 600px"
                )
            }
        }
    }

    fragment HomepageV3RepeatableIntegrations on PrismicHomepageV3RepeatableDataBodyIntegrations {
        __typename
        primary {
            title
            cta_text
            cta_link {
                ...PrismicLink
            }
            more_text
            logo_1 {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            logo_1_link {
                ...PrismicLink
            }
            logo_2 {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            logo_2_link {
                ...PrismicLink
            }
            logo_3 {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            logo_3_link {
                ...PrismicLink
            }
        }
    }

    fragment HomepageV3RepeatableCustomerLogosPlusCta on PrismicHomepageV3RepeatableDataBodyCustomerLogosPlusCta {
        __typename
        primary {
            title
            cta_text
            cta_link {
                ...PrismicLink
            }
            background_color
        }
        items {
            logo {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
        }
    }

    fragment HomepageV3RepeatableVideoTabs on PrismicHomepageV3RepeatableDataBodyVideoTabs {
        __typename
        items {
            icon {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            title
            description
            poster {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            video_mp4 {
                ...Video
            }
            video_webm {
                ...Video
            }
        }
    }

    fragment HomepageV3RepeatableVideoWithOutline on PrismicHomepageV3RepeatableDataBodyVideoWithOutline {
        __typename
        primary {
            title
        }
        items {
            description
            name_title
            video_id
            thumbnail {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(
                    breakpoints: $imageBreakpoints
                    sizes: "(max-width: 991px) calc(100vw - 48px), (max-width: 1439px) calc(33vw - 220px), 367px"
                )
            }
            customer_logo {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
        }
    }

    fragment HomepageV3RepeatableRotatingBanner on PrismicHomepageV3RepeatableDataBodyRotatingBanner {
        __typename
        items {
            text
        }
        primary {
            cta_text
            cta_link {
                ...PrismicLink
            }
            size_variant
            bg_color
        }
    }

    fragment HomepageV3RepeatableSignupReason on PrismicSignupReason {
        data {
            primary
            secondary
            icon {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
        }
    }

    fragment HomepageV3RepeatableHero on PrismicHomepageV3RepeatableDataBodyHero {
        __typename
        primary {
            section_html_id
            title
            inverse
            lead_text {
                raw
            }
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumnsAsymmetric)
            }
            wistia_video_id
            rotating_signup_reasons
            submit_label
            signup_reason_1 {
                document {
                    ...SignupReason
                }
            }
            signup_reason_2 {
                document {
                    ...SignupReason
                }
            }
            signup_reason_3 {
                document {
                    ...SignupReason
                }
            }
            video_mp4 {
                ...Video
            }
            video_webm {
                ...Video
            }
            predefined_signup_banner {
                document {
                    ...SignupBanner
                }
            }
        }
    }

    fragment HomepageV3RepeatableStatistics on PrismicHomepageV3RepeatableDataBodyStats {
        __typename
        primary {
            first_stat_amount
            first_stat_text
            first_stat_as_percentile
            second_stat_amount
            second_stat_text
            second_stat_as_percentile
            third_stat_amount
            third_stat_text
            third_stat_as_percentile
            source_text {
                raw
            }
            predefined_statistics {
                document {
                    ... on PrismicStatisticsSection {
                        data {
                            first_stat {
                                amount
                                text
                                as_percentile
                            }
                            second_stat {
                                amount
                                text
                                as_percentile
                            }
                            third_stat {
                                amount
                                text
                                as_percentile
                            }
                            source_text {
                                raw
                            }
                        }
                    }
                }
            }
        }
    }

    fragment HomepageV3RepeatableCardGrid on PrismicHomepageV3RepeatableDataBodyCardGrid {
        __typename
        primary {
            blockTitle: title {
                raw
            }
            subtitle
            show_more_text
            initial_cards_shown
            initial_cards_shown_on_mobile
            initial_cards_shown_on_desktop
            card_cta_text
            show_filter
            layout
        }
        items {
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            title
            body
            ctaLink: cta_link {
                ...PrismicLink
            }
            filterKeywords: filter_keywords
            tag_text
            tag_color
        }
    }

    fragment HomepageV3RepeatableGetStarted on PrismicHomepageV3RepeatableDataBodyGetStarted {
        __typename
        primary {
            title
            error_message
            button_label
            input_placeholder
            form_subtitle
            aria_label
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            predefined_signup_banner {
                document {
                    ...SignupBanner
                }
            }
        }
    }

    fragment HomepageV3RepeatableFeatureBlock on PrismicHomepageV3RepeatableDataBodyFeatureBlock {
        __typename
        primary {
            title_over_section
            title
            inverse
            background
            headline
            description {
                raw
            }
            pre_cta_links {
                raw
            }
            cta_label
            is_circular_image
            predefined_feature_block {
                document {
                    ... on PrismicFeatureBlock {
                        data {
                            image {
                                alt
                                copyright
                                url
                                dimensions {
                                    height
                                    width
                                }
                                gatsbyImageData(
                                    breakpoints: $imageBreakpoints
                                    sizes: $twoColumnsAsymmetric
                                )
                            }
                            title
                            subtitle
                            body {
                                raw
                            }
                        }
                    }
                }
            }
            cta_link {
                ...PrismicLink
            }
            cta_hash_link
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumnsAsymmetric)
            }
            wistia_video_id
            video_mp4 {
                ...Video
            }
            video_webm {
                ...Video
            }
        }
    }

    fragment HomepageV3RepeatableContentPlusBlocks on PrismicHomepageV3RepeatableDataBodyContentPlusBlocks {
        __typename
        primary {
            background
            plainTitle: title
            text_under_title {
                raw
            }
        }
        items {
            block_image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            block_title
            block_content {
                raw
            }
        }
    }

    fragment HomepageV3RepeatableSocialProof on PrismicHomepageV3RepeatableDataBodySocialProof {
        __typename
        primary {
            inverse
            title
        }
        items {
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumnsAsymmetric)
            }
            quote
            full_name
            person_byline
            predefined_testimonial {
                document {
                    ... on PrismicTestimonial {
                        data {
                            image {
                                alt
                                copyright
                                url
                                dimensions {
                                    height
                                    width
                                }
                                gatsbyImageData(
                                    breakpoints: $imageBreakpoints
                                    sizes: $twoColumnsAsymmetric
                                )
                            }
                            quote
                            attribution_name
                            attribution_title
                        }
                    }
                }
            }
        }
    }

    fragment HomepageV3RepeatableRatingsPlusHeadline on PrismicHomepageV3RepeatableDataBodyRatingsPlusHeadline {
        __typename
        primary {
            ratings_title {
                raw
            }
            predefined_ratings {
                document {
                    ... on PrismicSocialProofRatingsSection {
                        data {
                            title {
                                raw
                            }
                            ratings {
                                rating {
                                    document {
                                        ... on PrismicRating {
                                            data {
                                                icon: logo {
                                                    alt
                                                    copyright
                                                    url
                                                    dimensions {
                                                        height
                                                        width
                                                    }
                                                }
                                                stars
                                                rating_number
                                                link
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        items {
            rating {
                document {
                    ... on PrismicRating {
                        data {
                            icon: logo {
                                alt
                                copyright
                                url
                                dimensions {
                                    height
                                    width
                                }
                            }
                            stars
                            rating_number
                            link
                        }
                    }
                }
            }
        }
    }

    fragment HomepageV3RepeatableFAQ on PrismicHomepageV3RepeatableDataBodyFaq {
        __typename
        primary {
            title
            collapsible
        }
        items {
            question
            answer {
                raw
            }
            image {
                dimensions {
                    height
                    width
                }
                alt
                copyright
                url
            }
            icon {
                dimensions {
                    height
                    width
                }
                alt
                copyright
                url
            }
        }
    }

    fragment HomepageV3RepeatablePricing on PrismicHomepageV3RepeatableDataBodyPricing {
        __typename
        primary {
            pricingTitle: title {
                raw
            }
            pricing_tiers {
                document {
                    ...Pricing
                }
            }
        }
    }

    fragment HomepageV3RepeatableFeatureGrid on PrismicHomepageV3RepeatableDataBodyFeatureGrid {
        __typename
        primary {
            title
            background
        }
        items {
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumnsAsymmetric)
            }
            feature_title
            feature_description {
                raw
            }
            predefined_feature {
                document {
                    ... on PrismicFeatureIconCard {
                        data {
                            icon {
                                alt
                                copyright
                                url
                                dimensions {
                                    height
                                    width
                                }
                                gatsbyImageData(
                                    breakpoints: $imageBreakpoints
                                    sizes: $twoColumnsAsymmetric
                                )
                            }
                            title
                            body
                        }
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(HomepageV3Repeatable)
